/**
 * Cart drawer enhancements
 * ------------------------------------------------------------
 * Adds behaviour for the in-drawer upsell carousel:
 *   - [data-cart-upsell-add]   : add the product to the cart, then ask the
 *                                theme's cart controller to re-render the drawer
 *   - [data-cart-upsell-prev]  : scroll the upsell track left
 *   - [data-cart-upsell-next]  : scroll the upsell track right
 *
 * Listeners are delegated from the document so they keep working after the
 * mini-cart re-renders (the theme replaces the drawer's innerHTML on change).
 */
(function () {
  'use strict';

  function refreshCart() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { scrollToTop: false }
      })
    );
  }

  function addUpsell(button) {
    var variantId = button.getAttribute('data-variant-id');

    if (!variantId || button.hasAttribute('disabled')) {
      return;
    }

    var addUrl = (window.routes && window.routes.cartAddUrl) || '/cart/add';

    button.setAttribute('disabled', 'disabled');
    button.classList.add('is-loading');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    fetch(addUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // Works around a Shopify quirk where this header is expected on AJAX cart adds
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (response) {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));

        if (response.ok) {
          // The drawer re-renders entirely, discarding this (now stale) button.
          refreshCart();
        } else {
          button.removeAttribute('disabled');
          button.classList.remove('is-loading');
        }
      })
      .catch(function () {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        button.removeAttribute('disabled');
        button.classList.remove('is-loading');
      });
  }

  /**
   * TrackTail tracker upgrade: swap the "zonder tracker" line for the same-colour
   * variant WITH a tracker. Shopify can't change a line's variant in place, and a
   * line item's key hash is regenerated on every cart mutation — so a two-step
   * "add then remove-by-key" leaves the old line behind (the key goes stale after
   * the add). Instead we do ONE atomic /cart/update.js keyed by VARIANT ID: set the
   * old variant to 0 and the new variant to the carried-over quantity in a single
   * request, which never depends on a volatile line key.
   */
  function upgradeTracker(button) {
    var newVariantId = button.getAttribute('data-new-variant-id');
    var oldVariantId = button.getAttribute('data-old-variant-id');
    var qty = parseInt(button.getAttribute('data-qty'), 10) || 1;

    if (!newVariantId || !oldVariantId || button.hasAttribute('disabled')) {
      return;
    }

    var upsell = button.closest('[data-tracker-upsell]');
    if (upsell) {
      upsell.querySelectorAll('[data-tracker-upgrade]').forEach(function (b) {
        b.setAttribute('disabled', 'disabled');
      });
    }
    button.classList.add('is-loading');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    var updates = {};
    updates[oldVariantId] = 0;
    updates[newVariantId] = qty;

    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (response) {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        if (response && response.ok) {
          refreshCart();
        } else {
          throw new Error('update failed');
        }
      })
      .catch(function () {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        button.classList.remove('is-loading');
        if (upsell) {
          upsell.querySelectorAll('[data-tracker-upgrade]').forEach(function (b) {
            b.removeAttribute('disabled');
          });
        }
      });
  }

  function scrollUpsells(button) {
    var wrapper = button.closest('[data-cart-upsells]');
    if (!wrapper) {
      return;
    }

    var track = wrapper.querySelector('[data-cart-upsell-track]');
    if (!track) {
      return;
    }

    var isPrev = button.hasAttribute('data-cart-upsell-prev');
    var amount = Math.max(track.clientWidth * 0.8, 120);

    track.scrollBy({ left: isPrev ? -amount : amount, behavior: 'smooth' });
  }

  /**
   * Slide-cart backdrop + scroll lock.
   * The theme only toggles #mini-cart[aria-hidden] when opening/closing, so we
   * mirror that onto <body> to drive a full-page backdrop and lock scrolling.
   * Clicking the backdrop falls through to the theme's own "click outside the
   * cart" handler, which closes the drawer.
   */
  function initSlideCart() {
    var miniCart = document.getElementById('mini-cart');
    if (!miniCart || document.querySelector('.mini-cart-backdrop')) {
      return;
    }

    var backdrop = document.createElement('div');
    backdrop.className = 'mini-cart-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');

    // Append the backdrop inside the header's stacking context (the same one the
    // fixed cart panel lives in) so the panel can sit above it. Placing it on
    // <body> would trap the panel below the backdrop -> full grey screen.
    var stackingRoot = miniCart.closest('.shopify-section') || document.body;
    stackingRoot.appendChild(backdrop);

    // The theme's "click outside" close (_onWindowClick) only fires for targets
    // OUTSIDE the header section. Because the backdrop lives inside that section,
    // it never triggers that handler, so we close the drawer explicitly here by
    // re-clicking the toggle (which closes when the drawer is open).
    backdrop.addEventListener('click', function () {
      var toggle = document.querySelector('[data-action="toggle-mini-cart"]');
      if (toggle) {
        toggle.click();
      }
    });

    function sync() {
      var isOpen = miniCart.getAttribute('aria-hidden') === 'false';
      document.body.classList.toggle('mini-cart-is-open', isOpen);
    }

    // The theme replaces the drawer's innerHTML on cart changes but keeps the
    // #mini-cart element itself, so this observer stays valid across re-renders.
    new MutationObserver(sync).observe(miniCart, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });

    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSlideCart);
  } else {
    initSlideCart();
  }

  document.addEventListener('click', function (event) {
    var addButton = event.target.closest('[data-cart-upsell-add]');
    if (addButton) {
      event.preventDefault();
      addUpsell(addButton);
      return;
    }

    var upgradeButton = event.target.closest('[data-tracker-upgrade]');
    if (upgradeButton) {
      event.preventDefault();
      upgradeTracker(upgradeButton);
      return;
    }

    var navButton = event.target.closest('[data-cart-upsell-prev], [data-cart-upsell-next]');
    if (navButton) {
      event.preventDefault();
      scrollUpsells(navButton);
    }
  });
})();
